import {
  Box,
  Paper,
  Button,
  Badge,
  Typography,
  IconButton,
  Theme,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import RoomIcon from "@mui/icons-material/Room";
import { DispatchEventFeedModel, IRootReducer } from "../../../reducers";
import GridLoadingOverlay from "../../common/loading-overlay";
import { TruncateSourceIdUtil } from "../../../utils/string-utils";
import {
  AgencyCode,
  AreaCode,
  RegionCode,
} from "../../../reducers/states/agency";
import AdditionalDetailsModal from "../additional-details-modal";
import { Link } from "react-router-dom";
import IDFGridHeader from "./idf-grid-header";
import { useDispatch, useSelector } from "react-redux";
import { DictionaryFromArrayUtil } from "../../../utils/array-utils";
import { getDispatchFeedEventRequest, getDispatchFeedEventsRequest } from "../../../actions";
import { ShortIdUtil } from "../../../utils/id-utils";
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridOverlay,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import { FeedView } from "../../../enums/feed-view";
import { AgencyChip } from "../../common/agency-chip";
import { setDispatchDataGridGeoRequest, setSelectedEventIdsRequest, updateViewportRequest } from "../../../actions/map";
import _ from "lodash";
import { renderCellExpand } from "../../common/data-grid/cell-expander";
import { UTCFromTimeFrameUtil, UTCToOneMinuteAheadUtil } from "../../../utils/date-utils";
import { ZoomOutMap } from "@mui/icons-material";
import PanelActionIcon from "../../common/panel-collapse-icon";
import { getWspDistrictAgencyId, WspAgencyLabel } from "../../../reducers/states/agency/wsp";

const useStyles = makeStyles((theme: Theme) => ({
  pinnedEvent: {
    backgroundColor: theme.pins.PinHighlight,
  },
  mapIconOpen: {
    color: "black",
  },
  mapIconClosed: {
    color: theme.marker.ClosedDispatchEventIFD,
  },
  eventNumberOpen: {
    color: theme.palette.primary.main,
  },
  eventNumberClosed: {
    color: theme.marker.ClosedDispatchEventIFD,
  },
  closedRow: {
    color: theme.marker.ClosedDispatchEventIFD,
  },
  openRow: {
    backgroundColor: "white",
  },
  badge: {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const defaultFilter: string[] = [WspAgencyLabel.SEA_D2, AgencyCode.SFD, AgencyCode.SPD, AgencyCode.KCM];

export interface DispatchGridRow {
  id: string;
  eventNumber: string;
  eventStartDate: string;
  lastUpdateDate: string;
  agency: string;
  area?: string;
  region?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  eventType?: string;
  jogIds?: string;
  associatedIncId?: string;
  noteworthyReason?: string;
  status?: string;
}

interface DispatchEventFeedGridProps {
  props: {
    isDashDisplay?: boolean;
    isScreenLessThanLarge?: boolean;
    isSideDisplay?: boolean;
    isCollapsed?: boolean;
    title?: string;
    handleDashCenter?: () => void;
    handleDashCollapse?: () => void;
  };
}

const DispatchEventFeedGrid: FC<DispatchEventFeedGridProps> = ({ props }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [mainGridData, setMainGridData] = useState([] as DispatchGridRow[]);
  const [pinGridData, setPinGridData] = useState([] as DispatchGridRow[]);
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [visibleDataGridRows, setVisibleDataGridRows] = useState({});
  const [gridFilterModel, setGridFilterModel] = useState({} as GridFilterModel);
  const [dataGridFilteredRowIds, setDataGridFilteredRowIds] = useState([] as string[]);
  const [filterByDataGrid, setFilterByDataGrid] = useState(false);
  const [addtlDetailsOpen, setAddtlDetailsOpen] = useState(false);
  const [pinPaginationModel, setPinPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  const [idfPaginationModel, setIdfPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const [addtlDetails, setAddtlDetails] = useState({} as DispatchEventFeedModel);
  const [mostRecentSelectedEvent, setMostRecentSelectedEvent] = useState({} as DispatchEventFeedModel);
  const [selectedEvents, setSelectedEvents] = useState([] as DispatchEventFeedModel[]);
  const [selectedPinnedEvents, setSelectedPinnedEvents] = useState([] as DispatchEventFeedModel[]);

  const upperContainerRef = useRef<any>(null);
  const dispatchStore = useSelector((state: IRootReducer) => state.dispatchEventFeedReducer);
  const jogFilters = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.jogFilters);
  const timeFrame = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.timeFrame);
  const agencyFilters = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.agencyFilters);
  const feedView = useSelector((state: IRootReducer) => state.userInfoReducer.userPreferences.feedView);
  const showOpenDispatchesOnly = useSelector(
    (state: IRootReducer) => state.userInfoReducer.userPreferences.showOpenDispatchesOnly,
  );

  // Init map selected - clear selection on initial load
  useEffect(() => {
    dispatch(setSelectedEventIdsRequest(DictionaryFromArrayUtil([] as string[])));
  }, [dispatch]);

  // Update additional details
  useEffect(() => {
    setAddtlDetails(dispatchStore.dispatchEvent);
  }, [dispatchStore.dispatchEvent]);

  // Update gridData from latest dispatch events loaded
  useEffect(() => {
    const isWspDistrictSelected = (district: AreaCode | undefined): boolean => {      
      let filter = district && getWspDistrictAgencyId(district);
      return !!filter && agencyFilters.includes(filter as string);
    }

    const isAgencySelected = (event: DispatchEventFeedModel): boolean => {
      return event.agency === AgencyCode.WSP ?
        isWspDistrictSelected(event.areaCode as AreaCode) : agencyFilters.includes(event.agency);
    };

    setIsGridLoading(true);
    let gridRows: DispatchGridRow[] = [];

    if (dispatchStore.dispatchEvents.data) {
      gridRows = dispatchStore.dispatchEvents.data.reduce(function (
        results: DispatchGridRow[],
        event: DispatchEventFeedModel,
        index,
      ) {
        if (isAgencySelected(event)) {
          results.push({
            id: index + event.eventNumber,
            eventNumber: event.eventNumber,
            eventStartDate: event.eventDate,
            lastUpdateDate: event.lastUpdateDt,
            agency: event.agency,
            area: event.areaCode,
            region: event.regionCode,
            location: event.location,
            latitude: event.latitude,
            longitude: event.longitude,
            eventType: event.eventType,
            jogIds: event.jogIds,
            associatedIncId: event.associatedIncId,
            noteworthyReason: event.noteworthyReason,
            status: event.status,
          });
        }
        return results;
      }, []);
    }

    // Apply any datagrid filters if set and then update the redux store for dispatch events that should be displayed
    if (filterByDataGrid) {
      let filteredMapData = gridRows.filter((r) => dataGridFilteredRowIds.includes(r.id));
      dispatch(setDispatchDataGridGeoRequest(filteredMapData));
    } else {
      dispatch(setDispatchDataGridGeoRequest(gridRows));
    }

    let pinRows: DispatchGridRow[] = dispatchStore.pinnedEvents.reduce(function (
      results: DispatchGridRow[],
      event: DispatchEventFeedModel,
      index,
    ) {
      if (isAgencySelected(event)) {
        results.push({
          id: index + event.eventNumber,
          eventNumber: event.eventNumber,
          eventStartDate: event.eventDate,
          lastUpdateDate: event.lastUpdateDt,
          agency: event.agency,
          location: event.location,
          latitude: event.latitude,
          longitude: event.longitude,
          eventType: event.eventType,
          associatedIncId: event.associatedIncId,
          noteworthyReason: event.noteworthyReason,
          status: event.status,
        });
      }
      return results;
    }, []);

    // Filter out User Pins from the main feed
    let pinEventNumbers = pinRows.map((p) => p.eventNumber);
    setMainGridData([...gridRows.filter((g) => !pinEventNumbers.includes(g.eventNumber))]);
    setPinGridData([...pinRows]);
    setIsGridLoading(false);
  }, [
    dispatchStore.dispatchEvents.data,
    dispatchStore.pinnedEvents,
    feedView,
    agencyFilters,
    dispatch,
    dataGridFilteredRowIds,
    filterByDataGrid,
  ]);

  const showToolbar = (): JSX.Element | null => {
    if (!props?.isDashDisplay) {
      return (
        <GridToolbarContainer>
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      );
    }
    return null;
  };

  const handleDispatchPaging = () => {
    dispatch(
      getDispatchFeedEventsRequest(
        jogFilters.join(","), // todo: will need further update to support JOG based paging
        UTCFromTimeFrameUtil(timeFrame),
        UTCToOneMinuteAheadUtil(),
        showOpenDispatchesOnly,
        false, // Set map data
        true,
      ), // Set paging
    );
  };

  const handleEventSelection = (
    rowIds: GridRowSelectionModel,
    gridData: DispatchGridRow[],
    setSelectedEvents: Function,
  ) => {
    const selectedRowsEventNumbers: string[] = gridData.filter((d) => rowIds.includes(d.id)).map((s) => s.eventNumber);

    const selectedEvents: DispatchEventFeedModel[] = dispatchStore.dispatchEvents.data.filter((d) =>
      selectedRowsEventNumbers.includes(d.eventNumber),
    );

    setSelectedEvents(selectedEvents);

    setMostRecentSelectedEvent(
      selectedEvents.find((d) => d.eventNumber === selectedRowsEventNumbers[selectedRowsEventNumbers.length - 1])!,
    );

    if (props.isDashDisplay) {
      dispatch(setSelectedEventIdsRequest(DictionaryFromArrayUtil(selectedRowsEventNumbers)));
    }
  };

  const handleAdditionalDetailsOpen = (id: string) => {
    dispatch(getDispatchFeedEventRequest(id));
    setAddtlDetailsOpen(true);
  };

  // TODO: This function is not being referenced. Is this a bug or old code we can delete?
  // Handle DataGrid filters - DataGrid hides rows without changing mainGridData
  const handleVisibleRowsChange = (visibleRowsLookup: Object) => {
    if (!_.isEqual(visibleRowsLookup, visibleDataGridRows)) {
      if (
        _.isEmpty(visibleRowsLookup) &&
        (gridFilterModel?.items?.length === 0 || gridFilterModel?.items[0]?.value === "")
      ) {
        // No items and no filterModel in this array means none are hidden by the grid, update the map
        // to what is currently stored in mainGridData
        setDataGridFilteredRowIds([]);
        setFilterByDataGrid(false);
      } else {
        let visibleRowIds: string[] = _.keys(_.pickBy(visibleRowsLookup));
        setDataGridFilteredRowIds(visibleRowIds);
        setFilterByDataGrid(true);
      }

      setVisibleDataGridRows(visibleRowsLookup);
    }
  };

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMoreThanSmall = useMediaQuery(theme.breakpoints.up("sm"));
  const isLessThanLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  const showDefaultFields = !props.isDashDisplay || !props.isCollapsed;
  const showExtendedFields =
    !props.isDashDisplay || (!props.isCollapsed && !isXs && (isLessThanLg || (!props.isSideDisplay && (isLg || isXl))));

  const columnVisibility = {
    agency: true,
    zoomTo: !!props?.isDashDisplay,
    openDetails: true,

    eventStartDate: !props?.isDashDisplay,
    eventType: showDefaultFields || isXs,
    location: showDefaultFields,

    associatedIncId: showExtendedFields && feedView !== FeedView.NOTEWORTHY,
    eventNumber: showExtendedFields,
    lastUpdateDate: showExtendedFields,
    noteworthyReason: showExtendedFields && feedView !== FeedView.ALL,
  };

  const getFormattedTime = (datetime: string) => {
    if (datetime) return moment(datetime).format("HH:mm:ss");
    return "Unspecified";
  };

  const getDetailsTooltip = (row: DispatchGridRow) => {
    const mainMsg = "Click to open additional details";
    return (
      <span style={{ whiteSpace: "pre-line" }}>
        {mainMsg}
        <br />
        <br />
        Event Number: {TruncateSourceIdUtil(row.eventNumber as string, row.agency as string)}
        <br />
        <br />
        Location: {row.location}
        <br />
        Type: {row.eventType}
        <br />
        <br />
        Started: {getFormattedTime(row.eventStartDate)}
        <br />
        Updated: {getFormattedTime(row.lastUpdateDate)}
        <br />
      </span>
    );
  };

  const columns: GridColDef[] = [
    {
      field: "zoomTo",
      headerName: "Map",
      cellClassName: "narrow-cell",
      headerClassName: "narrow-cell",
      maxWidth: 40,
      minWidth: 40,
      filterable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",      
      renderCell: (params: GridRenderCellParams) => {
        const latitude = params.row.latitude;
        const longitude = params.row.longitude;
        const status = params.row.status;
        if (latitude) {
          return (
            <Tooltip title="Zoom to Dispatch Event on Map">
              <IconButton
                onClick={() => dispatch(updateViewportRequest(latitude as number, longitude as number, 14))}
                size="large"
              >
                {(status as string) === "CLOSED" ? (
                  <RoomIcon className={classes.mapIconClosed} />
                ) : (
                  <RoomIcon className={classes.mapIconOpen} />
                )}
              </IconButton>
            </Tooltip>
          );
        }
      },
    },
    {
      field: "openDetails",
      headerName: "Detail",
      cellClassName: "narrow-cell",
      headerClassName: "narrow-cell",
      maxWidth: 40,
      minWidth: 40,
      filterable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={getDetailsTooltip(params.row)}>
            <IconButton
              onClick={() => handleAdditionalDetailsOpen(params.row.eventNumber as string)}
              size="large"
            >
              <ZoomOutMap />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "eventNumber",
      headerName: props.isDashDisplay && isMoreThanSmall ? "Event #" : "Event Number",
      flex: 1,
      maxWidth: 150,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const sourceId = params.row.eventNumber;
        const agency = params.row.agency;
        const associatedIncId = params.row.associatedIncId;
        const status = params.row.status;
        return (
          <Badge
            style={{ top: "10px" }}
            badgeContent={associatedIncId ? 1 : 0}
            classes={{ badge: classes.badge }}
            variant="dot"
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <Tooltip title="Click to open additional details">
              <Button
                style={{ top: "-10px" }}
                type="button"
                className={(status as string) === "CLOSED" ? classes.eventNumberClosed : classes.eventNumberOpen}
                data-rum-id={`dispatch-grid__${sourceId}`}
                onClick={() => handleAdditionalDetailsOpen(sourceId as string)}
              >
                {TruncateSourceIdUtil(sourceId as string, agency as string)}
              </Button>
            </Tooltip>
          </Badge>
        );
      },
    },
    {
      field: "eventStartDate",
      headerName: props.isDashDisplay ? "Started" : "Start Time",
      maxWidth: 120,
      minWidth: 76,
      flex: 0.75,
      filterable: false,
      valueFormatter: (params: GridValueFormatterParams) => {
        return getFormattedTime(params.value);
      },
    },
    {
      field: "lastUpdateDate",
      headerName: props.isDashDisplay ? "Updated" : "Updated Time",
      flex: 0.75,
      filterable: false,
      maxWidth: 120,
      minWidth: 76,
      disableColumnMenu: true,
      description: "",
      valueFormatter: (params: GridValueFormatterParams) => {
        return getFormattedTime(params.value);
      },
    },
    {
      field: "agency",
      headerName: "Agency",
      align: "center",
      headerAlign: "center",
      cellClassName: "narrow-cell",
      headerClassName: "narrow-cell",
      filterable: false,
      sortable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      minWidth: props.isDashDisplay ? 78 : 90,
      maxWidth: props.isDashDisplay ? 78 : 90,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <AgencyChip
            props={{
              agency: params.row.agency as AgencyCode,
              area: params.row.area as AreaCode,
              region: params.row.region as RegionCode,
              isClosed: (params.row.status as string) === "CLOSED",
            }}
          />
        );
      },
    },
    { field: "eventType", headerName: "Type", flex: 1.25, renderCell: renderCellExpand },
    { field: "location", headerName: "Location", flex: 1.25, renderCell: renderCellExpand },
    {
      field: "noteworthyReason",
      headerName: "Noteworthy Reason",
      flex: 1.1,
      renderCell: (params: GridRenderCellParams) => {
        const assocIncId = params.row.associatedIncId;
        if (assocIncId) {
          return <Link to={"/incident-model/" + assocIncId}>Associated VCC IM</Link>;
        } else {
          return params.row.noteworthyReason;
        }
      },
    },
    {
      field: "associatedIncId",
      headerName: "VCC Incident",
      flex: 1.1,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const assocIncId = params.row.associatedIncId;
        return (
          <Link to={"/incident-model/" + assocIncId} data-rum-id={`incident-model-grid-${assocIncId}`}>
            {assocIncId ? ShortIdUtil(assocIncId.toString()) : ""}
          </Link>
        );
      },
    },
  ];

  const noPinned = useMemo(() => <GridOverlay>No pinned dispatches</GridOverlay>, []);

  const noFilteredPin = useMemo(() => <GridOverlay>No filtered pinned results</GridOverlay>, []);

  const noRecent = useMemo(
    () => <GridOverlay>{feedView === "All" ? "No recent dispatches" : "No noteworthy dispatches"}</GridOverlay>,
    [feedView],
  );

  const noFiltered = useMemo(() => <GridOverlay>No filtered dispatch results</GridOverlay>, []);

  const getGridStyle = (rowCount: number) => {
    return {
      // Wrap column headers
      "& .MuiDataGrid-columnHeaderTitle": {
        whiteSpace: "normal",
        lineHeight: "normal",
        padding: "2px 0",
      },
      "& .MuiDataGrid-columnHeader": {
        height: "unset !important",
      },

      // Tighten up width of some columns for better side panel display
      "& .MuiIconButton-root": {
        padding: "8px",
      },
      
      "& .MuiDataGrid-cell.narrow-cell": {
        padding: "0",
      },

      "& .MuiDataGrid-columnHeader.narrow-cell": {
        padding: "0",
      },

      // Fix issue of hidden "No events" message
      "& .MuiDataGrid-virtualScroller": {
        overflow: rowCount ? "auto" : "unset",
      },

      // Reduce padding to avoid horizontal scrollbar for paging controls
      // This does not avoid it when the panel is collapsed, only expanded
      "& .MuiToolbar-root.MuiTablePagination-toolbar": {
        paddingLeft: "16px",
      },
    };
  };

  const getGridHeight = () => {
    const header = 64;
    const appMainBoxMargins = 16 + 8;
    const upperContainer = upperContainerRef.current?.clientHeight ?? 0;
    const loadMoreButton = dispatchStore.dispatchEvents.pagingKeys ? 40 : 0;
    const total = header + appMainBoxMargins + upperContainer + loadMoreButton;
    return `calc(100vh - ${total}px`;
  };

  return (
    <>
      <Box ref={upperContainerRef}>
        {props.isDashDisplay && (
          <Box id="events-column-header" display="flex" justifyContent="center">
            <Typography style={{ margin: "auto", textAlign: "center" }} width={"100%"} variant="h6">
              {props?.isCollapsed ? "Events" : "Recent Dispatch Events"}
            </Typography>

            {props?.isSideDisplay && props?.handleDashCollapse && !props.isScreenLessThanLarge && (
              <PanelActionIcon iconType="left" isActive={props.isCollapsed} onClick={props.handleDashCollapse} />
            )}

            {!props.isCollapsed && props?.handleDashCenter && (
              <PanelActionIcon
                isActive={props.isSideDisplay}
                onClick={props.handleDashCenter}
                iconType={props.isSideDisplay ? (props.isScreenLessThanLarge ? "down" : "center") : (props.isScreenLessThanLarge ? "up" : "left")}
                tooltipWhenActive={`Move ${props.isScreenLessThanLarge ? "down" : "to center"}`}
                tooltipWhenInactive={`Move ${props.isScreenLessThanLarge ? "up" : "to side"}`}
              />
            )}
          </Box>
        )}

        {/* Filters */}
        {!props.isCollapsed && (
          <Paper variant="outlined">
            <IDFGridHeader
              props={{
                mostRecentSelectedEvent: mostRecentSelectedEvent,
                selectedIDFEvents: selectedEvents,
                selectedPinnedEvents: selectedPinnedEvents,
                isDashDisplay: props.isDashDisplay,
                isSideDisplay: props.isSideDisplay,
              }}
            />
          </Paper>
        )}

        {/* Pinned Grid */}
        {(!props?.isCollapsed || pinGridData.length > 0) && (
          <Box id="pinned-events-outer-box" mt={1}>
            <DataGrid
              rows={pinGridData}
              checkboxSelection={!props?.isCollapsed}
              columns={columns}
              columnVisibilityModel={columnVisibility}
              density="compact"
              disableColumnSelector
              disableRowSelectionOnClick
              getRowClassName={() => classes.pinnedEvent}
              hideFooter={pinGridData.length <= pinPaginationModel.pageSize}
              loading={isGridLoading}
              onPaginationModelChange={setPinPaginationModel}
              onRowSelectionModelChange={(selection: GridRowSelectionModel) =>
                handleEventSelection(selection, pinGridData, setSelectedPinnedEvents)
              }
              paginationModel={pinPaginationModel}
              slots={{
                loadingOverlay: GridLoadingOverlay,
                toolbar: showToolbar,
                noRowsOverlay: () => noPinned,
                noResultsOverlay: () => noFilteredPin,
              }}
              sx={{
                ...getGridStyle(pinGridData.length),
                minHeight: props?.isDashDisplay ? "100px" : "156px",
                maxHeight: props?.isDashDisplay ? "156px" : "196px",
              }}
            />
          </Box>
        )}
      </Box>

      {/* Main Grid */}
      <Box
        id="main-events-outer-box"
        mt={1}
        style={{
          height: getGridHeight(),
          // Use "min-content" so this grid collapses narrower than the grid pagination controls, which aren't set to wrap.
          ...(props.isCollapsed && { maxWidth: "min-content" }),
        }}
      >
        <DataGrid
          rows={mainGridData}
          checkboxSelection={!props?.isCollapsed}
          columns={columns}
          columnVisibilityModel={columnVisibility}
          density="compact"
          disableColumnSelector={true}
          disableRowSelectionOnClick
          getRowClassName={(params) => {
            return params.row.status === "CLOSED" ? classes.closedRow : classes.openRow;
          }}
          hideFooter={!mainGridData.length}
          loading={isGridLoading}
          onFilterModelChange={(fm: GridFilterModel) => setGridFilterModel(fm)}
          onPaginationModelChange={setIdfPaginationModel}
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            handleEventSelection(selection, mainGridData, setSelectedEvents);
          }}
          pageSizeOptions={mainGridData.length <= 25 ? [] : [25, 50, 100]}
          paginationModel={idfPaginationModel}
          slots={{
            loadingOverlay: GridLoadingOverlay,
            noRowsOverlay: () => noRecent,
            noResultsOverlay: () => noFiltered,
            toolbar: showToolbar,
          }}
          sx={{
            ...getGridStyle(mainGridData.length),
            minHeight: props?.isDashDisplay ? "156px" : "196px",
          }}
        />
      </Box>

      {dispatchStore.dispatchEvents.pagingKeys && (
        <Button
          variant="contained"
          color="inherit"
          size="small"
          onClick={handleDispatchPaging}
          style={{ float: "right", marginTop: "8px", marginBottom: 0 }}
        >
          Load More Events
        </Button>
      )}

      <AdditionalDetailsModal
        props={{
          isOpen: addtlDetailsOpen,
          idfEvent: addtlDetails,
          handleClose: () => setAddtlDetailsOpen(false),
        }}
      />
    </>
  );
};

export default DispatchEventFeedGrid;
